import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from '@emotion/styled';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router';
import { HackathonData } from '../../Api';
import { useApiClient } from '../../hooks/useApiClient';
import AppToolbar from '../shared/AppToolbar';
import { Button, ButtonLink } from '../shared/Button';
import { Page, PageBody, PageTitle } from '../shared/Layout';
import theme from '../shared/theme';

type Props = { goToAdmin(hackathonId: number): void };

const AddHackathonComponent: FC<Props> = ({ goToAdmin }) => {
  const apiClient = useApiClient();
  const [isCreating, setIsCreating] = useState(false);
  const [hackathon, setHackathon] = useState<HackathonData>({
    name: '',
    start: getDefaultStartDate(),
    demoTime: getDefaultEndDate()
  });

  const update = useCallback(
    (update: Partial<HackathonData>) => {
      setHackathon(current => ({ ...current, ...update }));
    },
    [setHackathon]
  );

  const updateStartDate = useCallback(
    (newStart: Date) => {
      setHackathon(current => {
        // Calculate the time difference between old start and end
        const timeDiff = current.demoTime.getTime() - current.start.getTime();
        // Apply the same difference to the new start date to get the new end date
        const newEnd = new Date(newStart.getTime() + timeDiff);
        return { ...current, start: newStart, demoTime: newEnd };
      });
    },
    [setHackathon]
  );

  const isValid = useMemo(() => {
    if (hackathon.name.length === 0) return false;
    if (hackathon.start >= hackathon.demoTime) return false;
    return true;
  }, [hackathon]);

  const save = useCallback(() => {
    if (!isValid) return;
    async function save() {
      setIsCreating(true);
      try {
        const savedHackathon = await apiClient.hackathon.create(hackathon);
        if (savedHackathon) goToAdmin(savedHackathon.hackathonId);
      } catch (error) {
        console.error('Failed to create hackathon:', error);
        alert('Failed to create hackathon. Please try again.');
        setIsCreating(false);
      }
    }
    save();
  }, [isValid, hackathon, goToAdmin, apiClient.hackathon]);

  return (
    <>
      <Helmet>
        <title>Create Hackathon</title>
      </Helmet>
      <Page>
        <AppToolbar hackathonId={undefined} />
        <PageBody>
          <PageTitle>Create New Hackathon</PageTitle>
          <Form>
            <FormField>
              <Label htmlFor="name">Hackathon Name</Label>
              <NameInput
                id="name"
                type="text"
                value={hackathon.name}
                onChange={e => update({ name: e.target.value })}
                placeholder="Enter hackathon name (emojis supported! 🎉)"
                disabled={isCreating}
              />
            </FormField>
            <DateTimeRow>
              <DateTimeField>
                <Label htmlFor="start">Start Date & Time</Label>
                <Input
                  id="start"
                  type="datetime-local"
                  value={formatDateTimeLocal(hackathon.start)}
                  onChange={e => updateStartDate(new Date(e.target.value))}
                  disabled={isCreating}
                />
              </DateTimeField>
              <DateTimeField>
                <Label htmlFor="demoTime">End Date & Time</Label>
                <Input
                  id="demoTime"
                  type="datetime-local"
                  value={formatDateTimeLocal(hackathon.demoTime)}
                  onChange={e => update({ demoTime: new Date(e.target.value) })}
                  disabled={isCreating}
                />
                {hackathon.start >= hackathon.demoTime && (
                  <ValidationError>End date must be after start date</ValidationError>
                )}
              </DateTimeField>
            </DateTimeRow>
            <Footer>
              <CancelLink category="invisible" to="/admin">
                Cancel
              </CancelLink>
              <SaveButton category="success" disabled={!isValid || isCreating} onClick={save}>
                {isCreating ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create'}
              </SaveButton>
            </Footer>
          </Form>
        </PageBody>
      </Page>
    </>
  );
};

// Helper function to get default start date (today at midnight)
function getDefaultStartDate(): Date {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

// Helper function to get default end date (same day at 16:30)
function getDefaultEndDate(): Date {
  const date = new Date();
  // If current time is already past 16:30, set to tomorrow at 16:30
  if (date.getHours() > 16 || (date.getHours() === 16 && date.getMinutes() >= 30)) {
    date.setDate(date.getDate() + 1);
  }
  date.setHours(16, 30, 0, 0);
  return date;
}

// Helper function to format date for datetime-local input
function formatDateTimeLocal(date: Date): string {
  // Handle invalid dates
  if (isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 16);
}

const Form = styled.div`
  align-self: stretch;
  margin: 0 auto;
  padding: 30px;
  ${theme.styles.materialPanel}
  background: #fff;
`;

const FormField = styled.div`
  margin-bottom: 30px;
`;

const DateTimeRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
`;

const DateTimeField = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid ${theme.colors.lightGrey};
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.blue};
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const NameInput = styled(Input)`
  font-size: 20px;
  padding: 15px;
`;

const ValidationError = styled.div`
  color: ${theme.colors.red};
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-top: 30px;

  & > * {
    text-align: center;

    &:not(:last-child) {
      margin-right: 10px;
    }
  }
`;

const CancelLink = styled(ButtonLink)`
  width: 100px;
`;

const SaveButton = styled(Button)`
  width: 120px;
`;

export const AddHackathon: FC<RouteComponentProps> = ({ history }) => {
  const goToAdmin = useCallback(
    (hackathonId: number) => {
      history.push(`/hackathon/${hackathonId}/admin`);
    },
    [history]
  );

  return <AddHackathonComponent goToAdmin={goToAdmin} />;
};
